"use client";

import { m as motion } from "framer-motion";
import { Zap, CheckCircle2 } from "lucide-react";

interface MvpHeroVisualProps {
  isItalian: boolean;
}

const bars = [24, 38, 30, 52, 46, 68, 90];

export function MvpHeroVisual({ isItalian }: MvpHeroVisualProps) {
  return (
    <div className="relative w-full max-w-[340px] md:max-w-[380px] xl:max-w-[400px] mx-auto h-[420px] md:h-[460px] xl:h-[480px] pointer-events-none select-none">
      {/* Ambient glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72 rounded-full bg-primary/8 blur-3xl" />

      {/* Wireframe card - concept stage (background) */}
      <motion.div
        initial={{ opacity: 0, x: 16, y: 8 }}
        animate={{ opacity: 1, x: 0, y: 0 }}
        transition={{ delay: 0.5, duration: 0.7, ease: "easeOut" }}
        className="absolute top-4 right-0 w-[248px] rounded-2xl border border-[hsl(var(--border))] bg-surface/25 backdrop-blur-sm p-4"
      >
        {/* Browser chrome */}
        <div className="flex items-center gap-1.5 mb-4">
          <div className="w-2 h-2 rounded-full bg-muted-foreground/20" />
          <div className="w-2 h-2 rounded-full bg-muted-foreground/20" />
          <div className="w-2 h-2 rounded-full bg-muted-foreground/20" />
          <div className="flex-1 h-3 bg-muted-foreground/10 rounded-full ml-1.5" />
        </div>

        {/* Wireframe layout */}
        <div className="space-y-2">
          <div className="h-3 bg-muted-foreground/15 rounded w-3/5" />
          <div className="h-2 bg-muted-foreground/10 rounded w-full" />
          <div className="h-2 bg-muted-foreground/10 rounded w-5/6" />
          <div className="h-2 bg-muted-foreground/10 rounded w-4/6" />
          <div className="mt-1.5 h-[68px] bg-muted-foreground/6 rounded-xl border border-dashed border-muted-foreground/12 flex items-center justify-center">
            <div className="space-y-1.5 w-3/4">
              <div className="h-1.5 bg-muted-foreground/15 rounded-full" />
              <div className="h-1.5 bg-muted-foreground/10 rounded-full w-4/5 mx-auto" />
              <div className="h-1.5 bg-muted-foreground/10 rounded-full w-3/5 mx-auto" />
            </div>
          </div>
          <div className="flex gap-2">
            <div className="h-6 flex-1 bg-muted-foreground/8 rounded-lg" />
            <div className="h-6 w-14 bg-primary/12 rounded-lg" />
          </div>
        </div>

        <div className="mt-3 flex items-center gap-1.5">
          <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground/25" />
          <span className="text-[9px] font-mono text-muted-foreground/40 uppercase tracking-[0.14em]">
            Concept
          </span>
        </div>
      </motion.div>

      {/* Dashboard card - live product (foreground) */}
      <motion.div
        initial={{ opacity: 0, y: 28 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.7, ease: "easeOut" }}
        className="absolute bottom-4 left-0 w-[284px] rounded-2xl border border-primary/20 bg-surface shadow-[0_12px_56px_hsl(var(--primary)/0.18)] p-4"
      >
        {/* Browser chrome */}
        <div className="flex items-center gap-1.5 mb-4">
          <div className="w-2 h-2 rounded-full bg-red-400/65" />
          <div className="w-2 h-2 rounded-full bg-yellow-400/65" />
          <div className="w-2 h-2 rounded-full bg-green-400/65" />
          <div className="flex-1 mx-2 h-4 bg-background/80 rounded-full flex items-center px-2.5">
            <span className="text-[9px] text-muted-foreground/55 font-mono truncate">
              app.yourmvp.io
            </span>
          </div>
        </div>

        {/* Metric tiles */}
        <div className="grid grid-cols-3 gap-1.5 mb-3">
          {[
            { label: isItalian ? "Utenti" : "Users", val: "124" },
            { label: "Revenue", val: "€1.2k" },
            { label: "NPS", val: "72" },
          ].map((m) => (
            <div
              key={m.label}
              className="bg-background/60 rounded-xl p-2 text-center"
            >
              <div className="text-xs font-bold text-primary leading-none mb-0.5">
                {m.val}
              </div>
              <div className="text-[9px] text-muted-foreground/65 leading-none">
                {m.label}
              </div>
            </div>
          ))}
        </div>

        {/* Bar chart */}
        <div className="bg-background/40 rounded-xl p-2.5 mb-3">
          <div className="text-[9px] text-muted-foreground/55 mb-2 font-mono uppercase tracking-[0.12em]">
            {isItalian ? "Crescita" : "Growth"}
          </div>
          <div className="flex items-end gap-[3px] h-12">
            {bars.map((h, i) => (
              <motion.div
                key={i}
                initial={{ scaleY: 0 }}
                animate={{ scaleY: 1 }}
                transition={{
                  delay: 0.65 + i * 0.07,
                  duration: 0.35,
                  ease: "easeOut",
                }}
                style={{ height: `${h}%` }}
                className={`flex-1 rounded-[2px] origin-bottom ${
                  i === bars.length - 1
                    ? "bg-primary shadow-[0_0_10px_hsl(var(--primary)/0.55)]"
                    : i >= bars.length - 3
                    ? "bg-primary/35"
                    : "bg-primary/14"
                }`}
              />
            ))}
          </div>
        </div>

        {/* Live status bar */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[hsl(var(--success))] opacity-70" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[hsl(var(--success))]" />
            </span>
            <span className="text-[10px] text-muted-foreground">
              {isItalian ? "Live · settimana 4" : "Live · week 4"}
            </span>
          </div>
          <span className="text-[9px] text-primary font-mono">v1.0.0</span>
        </div>
      </motion.div>

      {/* Floating chip - timeline */}
      <motion.div
        initial={{ opacity: 0, x: -12 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.9, duration: 0.5, ease: "easeOut" }}
        className="absolute top-[37%] left-0 flex items-center gap-2 bg-surface border border-[hsl(var(--border))] rounded-full px-3 py-1.5 shadow-md backdrop-blur-sm"
      >
        <Zap className="w-3 h-3 text-primary shrink-0" />
        <span className="text-[11px] font-semibold text-foreground whitespace-nowrap">
          {isItalian ? "4-6 settimane" : "4-6 weeks"}
        </span>
      </motion.div>

      {/* Floating chip - MVP live */}
      <motion.div
        initial={{ opacity: 0, x: 12 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 1.1, duration: 0.5, ease: "easeOut" }}
        className="absolute bottom-[8%] right-0 flex items-center gap-1.5 bg-[hsl(var(--success)/0.1)] border border-[hsl(var(--success)/0.22)] rounded-full px-3 py-1.5 backdrop-blur-sm"
      >
        <CheckCircle2 className="w-3 h-3 text-[hsl(var(--success))] shrink-0" />
        <span className="text-[11px] font-semibold text-[hsl(var(--success))] whitespace-nowrap">
          MVP live
        </span>
      </motion.div>
    </div>
  );
}
